import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useProfile } from '@/hooks/useProfile'
import { useOnboarding } from '@/hooks/useOnboarding'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { DayCalendarView } from '@/components/dashboard/DayCalendarView'
import { ProfileTodoList } from '@/components/dashboard/ProfileTodoList'
import { ProfileCompletionWizard } from '@/components/dashboard/ProfileCompletionWizard'
import {
  Eye,
  Image,
  Briefcase,
  CheckCircle,
  User,
} from 'lucide-react'

const ONBOARDING_COMPLETED_KEY = 'onboarding_completed'

export default function Dashboard() {
  const { t } = useTranslation('dashboard')
  const { user } = useAuth()
  const { profile, completeness, updateProfile } = useProfile()
  const { completeOnboarding, shouldShowOnboarding } = useOnboarding()
  const [showWizard, setShowWizard] = useState(false)

  useEffect(() => {
    const onboardingCompleted = localStorage.getItem(ONBOARDING_COMPLETED_KEY)
    if (onboardingCompleted !== 'true') {
      const timer = setTimeout(() => {
        if (shouldShowOnboarding(completeness, profile)) {
          setShowWizard(true)
        }
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [completeness, profile, shouldShowOnboarding])

  const handleWizardClose = async () => {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true')
    completeOnboarding()
    setShowWizard(false)
    await updateProfile()
  }

  const stats = [
    {
      label: t('dashboard.stats.profileViews'),
      value: '0',
      icon: Eye,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      label: t('dashboard.stats.galleryImages'),
      value: profile?.gallery?.length || '0',
      icon: Image,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
    },
    {
      label: t('dashboard.stats.services'),
      value: profile?.services?.length || '0',
      icon: Briefcase,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
    },
    {
      label: t('dashboard.stats.profileStatus'),
      value: profile?.is_published ? t('dashboard.stats.published') : t('dashboard.stats.draft'),
      icon: CheckCircle,
      color: profile?.is_published ? 'text-green-500' : 'text-yellow-500',
      bgColor: profile?.is_published ? 'bg-green-50' : 'bg-yellow-50',
    },
  ]



  return (
    <>
      <div className="p-6 lg:p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            {t('dashboard.welcome', { name: user?.first_name || user?.username || 'there' })}
          </h1>
          <p className="mt-1 text-gray-500">
            {t('dashboard.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat) => {
            const Icon = stat.icon
            return (
              <Card key={stat.label}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{stat.label}</p>
                      <p className="text-2xl font-semibold">{stat.value}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>

        <div className="mb-8">
          <DayCalendarView />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <ProfileTodoList />

          <Card>
            <CardHeader>
              <CardTitle>{t('dashboard.quickActions.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to="/dashboard/profile/details">
                  <User className="w-4 h-4 mr-3" />
                  {t('dashboard.quickActions.editProfile')}
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to="/dashboard/profile/gallery">
                  <Image className="w-4 h-4 mr-3" />
                  {t('dashboard.quickActions.manageGallery')}
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to="/dashboard/offerings/services">
                  <Briefcase className="w-4 h-4 mr-3" />
                  {t('dashboard.quickActions.addService')}
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to={`/${profile?.slug}`} target="_blank">
                  <Eye className="w-4 h-4 mr-3" />
                  {t('dashboard.quickActions.previewProfile')}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {showWizard && (
        <ProfileCompletionWizard isOpen={showWizard} onComplete={handleWizardClose} />
      )}
    </>
  )
}
